import { computed, getCurrentInstance } from 'vue';
import router from '@/common/router';
import { confirmMsg } from '@/common/utils/commonUtils';
import { useInternational } from '@/common/locale';
import { USER_GENERAL_SETTING_ITEMS } from '@/common/utils';
import { checkRole } from '@/common/auth/auth.utils';
import { useMyInfoData } from './myInfoPopup.uses';
import type { GroupItem, LanguageItem, PopupListButtonData } from './myInfoPopup.types';
import { THEME_START_CASE } from './myInfoPopup.define';
import { useShowSubPopup } from '../popups.uses';

export interface Props {
  isFixed: boolean;
}

export interface Emit {
  (e: 'click-setting'): void;
  (e: 'mouseleave'): void;
}

export const setup = (props: Props, emit: Emit) => {
  const ctx = getCurrentInstance()!.appContext.config.globalProperties;
  const { t } = useInternational();

  const {
    userProfile,
    languageInfo,
    themeInfo,
    groupItems,

    selectedGroup,
    openedSubPanel,

    changeGroup,
    changeLanguage,
    changeTheme,
  } = useMyInfoData();

  const { mainMenuRef, subMenuRef, subMenuPopupStyle, setHoveredItemPosition } = useShowSubPopup();

  const isMaster = computed<boolean>(() => checkRole.isMaster(userProfile.value.role));

  const onLogout = () => {
    confirmMsg(ctx, {
      msgStr: t('MESSAGE.LOGOUT'),
      okCallback: () => router.push({ path: '/logout', query: { logoutTriggered: 'true' } }),
      confirmBtnText: t('WORD.GNB.PROFILE_LOGOUT'),
    });
  };

  const onChangeGroup = (group: GroupItem) => {
    changeGroup(group);
  };

  const onChangeLanguage = (language: LanguageItem) => {
    changeLanguage(language.name);
  };

  const onChangeTheme = (theme?: string) => {
    changeTheme(theme);
  };

  const onClickLanguage = (e?: MouseEvent) => {
    setHoveredItemPosition(e);
    openedSubPanel.value = openedSubPanel.value === 'language' ? null : 'language';
  };

  const onClickMonitoringGroup = (e?: MouseEvent) => {
    if (isMaster.value) {
      return;
    }

    setHoveredItemPosition(e);
    openedSubPanel.value = openedSubPanel.value === 'group' ? null : 'group';
  };

  const onClickOutside = () => {
    openedSubPanel.value = null;
  };

  const onClickSetting = () => {
    emit('click-setting');
  };

  const onLeavePopup = () => {
    if (props.isFixed) {
      return;
    }

    openedSubPanel.value = null;
    emit('mouseleave');
  };

  const POPUP_LIST_BUTTONS: PopupListButtonData[] = [
    { icon: 'icon-popup-theme', textKey: 'WORD.THEME', key: 'theme' },
    {
      icon: 'icon-popup-language',
      textKey: 'WORD.LANGUAGE',
      key: 'language',
      onClick: onClickLanguage,
    },
    {
      icon: 'icon-popup-group',
      textKey: 'WORD.MONITORING_GROUP',
      key: 'group',
      disabled: isMaster.value,
      onClick: onClickMonitoringGroup,
    },
    {
      icon: 'icon-popup-settings',
      textKey: 'WORD.GNB.SETTING',
      key: 'setting',
      onClick: onClickSetting,
    },
  ];

  const LANGUAGE_LIST: LanguageItem[] = USER_GENERAL_SETTING_ITEMS.lang;

  const THEME_OPTIONS = THEME_START_CASE.map((name) => ({
    label: name,
    text: name === 'Light' ? t('WORD.LIGHT') : t('WORD.DARK'),
  }));

  return {
    t,

    mainMenuRef,
    subMenuRef,
    subMenuPopupStyle,

    userProfile,
    languageInfo,
    themeInfo,
    groupItems,
    selectedGroup,

    openedSubPanel,

    POPUP_LIST_BUTTONS,
    LANGUAGE_LIST,
    THEME_OPTIONS,

    onClickOutside,
    onChangeGroup,
    onChangeLanguage,
    onChangeTheme,
    onLeavePopup,
    onLogout,
  };
};
